import { z } from "zod";
import {
  DOCUMENT_KEY,
  publishingDocumentSchema,
  readPublishingDocument,
} from "./document";

export const BOOK_KEY = "airtisan.book.v1";
export const LEAF_KEY = "airtisan.leaf.v1";
const label = z.string().trim().min(1).max(500);
const lessonSchema = z.object({ id: label, title: label }).strict();
const unitSchema = z
  .object({ id: label, title: label, lessons: z.array(lessonSchema).max(200) })
  .strict();
export const bookSchema = z
  .object({
    version: z.literal(1),
    title: label,
    widthMm: z.number().finite().min(40).max(1000),
    heightMm: z.number().finite().min(40).max(1000),
    marginMm: z.number().finite().min(0),
    units: z.array(unitSchema).max(200),
  })
  .strict()
  .superRefine((book, ctx) => {
    if (2 * book.marginMm >= Math.min(book.widthMm, book.heightMm)) {
      ctx.addIssue({
        code: "custom",
        message: "Margins leave no printable content area",
      });
    }
    const unitIds = book.units.map((u) => u.id);
    const lessonIds = book.units.flatMap((u) => u.lessons.map((l) => l.id));
    if (
      new Set(unitIds).size !== unitIds.length ||
      new Set(lessonIds).size !== lessonIds.length
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Unit and lesson IDs must be unique",
      });
    }
  });
const leafSchema = z
  .object({
    unitId: label,
    lessonId: label,
    order: z.number().int().nonnegative(),
  })
  .strict();
export type PublishingBook = z.infer<typeof bookSchema>;
export type LeafMembership = z.infer<typeof leafSchema>;
export type BookLeaf = LeafMembership & {
  componentId: string;
  name: string;
  occurrenceId?: string;
};
type BookSite = {
  flags: { [key: string]: string | boolean | number | null | undefined };
  components?: {
    uuid: string;
    name: string;
    metadata: { [key: string]: string };
  }[];
};

export function readBook(
  site: Pick<BookSite, "flags">,
): PublishingBook | undefined {
  const document = readPublishingDocument(site);
  if (document)
    return {
      version: 1,
      title: document.title,
      widthMm: document.defaults.trim.width,
      heightMm: document.defaults.trim.height,
      marginMm: document.defaults.margins.outer,
      units: document.units,
    };
  const value = site.flags[BOOK_KEY];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string")
    throw new Error("Invalid publishing book settings");
  return bookSchema.parse(JSON.parse(value));
}
export function writeBook(site: Pick<BookSite, "flags">, book: PublishingBook) {
  const parsed = bookSchema.parse(book);
  const document = readPublishingDocument(site);
  let nextDocument: string | undefined;
  if (document) {
    // Compatibility controls update canonical settings; title-only changes must
    // not flatten asymmetric page margins or erase per-page geometry.
    nextDocument = JSON.stringify(
      publishingDocumentSchema.parse({
        ...document,
        title: parsed.title,
        units: parsed.units,
        defaults: {
          ...document.defaults,
          trim: { width: parsed.widthMm, height: parsed.heightMm },
          margins:
            parsed.marginMm === document.defaults.margins.outer
              ? document.defaults.margins
              : {
                  top: parsed.marginMm,
                  outer: parsed.marginMm,
                  bottom: parsed.marginMm,
                  inner: parsed.marginMm,
                },
        },
      }),
    );
  }
  if (nextDocument !== undefined) site.flags[DOCUMENT_KEY] = nextDocument;
  site.flags[BOOK_KEY] = JSON.stringify(parsed);
}
export function writeLeaf(
  component: { metadata: { [key: string]: string } },
  leaf: LeafMembership,
) {
  component.metadata[LEAF_KEY] = JSON.stringify(leafSchema.parse(leaf));
}
export function getBookLeaves(site: BookSite): BookLeaf[] {
  const document = readPublishingDocument(site);
  if (document)
    return document.pages.flatMap((page, order) => {
      if (page.componentId === null) return [];
      const component = site.components?.find(
        (c) => c.uuid === page.componentId,
      );
      if (!component)
        throw new Error(`Page ${page.id} references a missing component`);
      return [
        {
          componentId: component.uuid,
          name: component.name,
          occurrenceId: page.id,
          unitId: page.unitId ?? "",
          lessonId: page.lessonId ?? "",
          order,
        },
      ];
    });
  const book = readBook(site);
  if (!book) return [];
  return (site.components ?? [])
    .filter((c) => c.metadata[LEAF_KEY] !== undefined)
    .map((c) => {
      const leaf = leafSchema.parse(JSON.parse(c.metadata[LEAF_KEY]));
      if (
        !book.units
          .find((u) => u.id === leaf.unitId)
          ?.lessons.some((l) => l.id === leaf.lessonId)
      ) {
        throw new Error(`Page ${c.name} references a missing unit or lesson`);
      }
      return { ...leaf, componentId: c.uuid, name: c.name };
    })
    .sort(
      (a, b) => a.order - b.order || a.componentId.localeCompare(b.componentId),
    );
}
export function getBookSpreads(
  leaves: BookLeaf[],
): { left?: BookLeaf; right?: BookLeaf }[] {
  if (!leaves.length) return [];
  const spreads: { left?: BookLeaf; right?: BookLeaf }[] = [
    { right: leaves[0] },
  ];
  for (let i = 1; i < leaves.length; i += 2)
    spreads.push({ left: leaves[i], right: leaves[i + 1] });
  return spreads;
}
export function defaultBook(title = "My book"): PublishingBook {
  return {
    version: 1,
    title,
    widthMm: 210,
    heightMm: 297,
    marginMm: 15,
    units: [
      {
        id: "unit-1",
        title: "Unit 1",
        lessons: [{ id: "lesson-1", title: "Lesson 1" }],
      },
    ],
  };
}
export { mmToCssPx } from "./units";
